import { FC, ReactElement, useEffect } from 'react';
import { Button, Icon, useBoolean } from '@wegroup/design-system';
import useQueryParams from '@common/src/hooks/useQueryParams';
import AnvaOfferSelectionOverviewButton from '@flows/src/components/flow-products/quotes/basket/actions/AnvaOfferSelectionOverviewButton';
import { useTranslation } from 'react-i18next';
import { useFlowsSession } from '@flows/src/api/getFlowsSessionById';
import useStartAnvaContractFlow from '@sales-conversation/src/hooks/anva/useStartAnvaContractFlow';
import useStartYellowhiveContractFlow from '@sales-conversation/src/hooks/yellowhive/useStartYellowhiveContractFlow';
import { useFlowInfo } from '@flows/src/api/getFlowInfo';
import YellowhiveSelectionOverviewEmailOfferButton from '@flows/src/components/flow-products/quotes/basket/actions/YellowhiveSelectionOverviewEmailOfferButton';

interface Props {
  flowId?: string;
}

const BottomBarSelectionOverviewActions: FC<Props> = ({ flowId }) => {
  const { t } = useTranslation();
  const queryParams = useQueryParams();
  const sessionId = queryParams.get('session_id') || '';
  const { data: session } = useFlowsSession(sessionId);
  const { data: flowInfo } = useFlowInfo(flowId || '');
  const startContractFlow = useStartAnvaContractFlow();
  const startYellowhiveContractFlow = useStartYellowhiveContractFlow();
  const [isLoading, setIsLoading] = useBoolean();

  const isAnvaFlow = flowInfo?.sub_type === 'ANVA';
  const isYellowhiveFlow = flowInfo?.sub_type === 'YELLOWHIVE';

  const handleNavigation = async () => {
    if (!flowId || !session) return;
    setIsLoading.on();
    if (isYellowhiveFlow) {
      await startYellowhiveContractFlow({
        flowId,
        yellowhive_products: session?.yellowhive_products || [],
        uuid: sessionId,
      });
    } else {
      await startContractFlow({
        flowId,
        anva_products: session.anva_products || [],
        uuid: sessionId,
      });
    }
    setIsLoading.off();
    return;
  };

  // Deep-link handling: if ?deeplink=slotvragen is present, start contract flow
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (isYellowhiveFlow && params.get('deeplink') === 'slotvragen') {
      // avoid repeated triggers by removing the param
      params.delete('deeplink');
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, '', newUrl);
      handleNavigation();
    }
  }, [isYellowhiveFlow]);

  let offerActionButton: ReactElement | null = null;
  if (isAnvaFlow) {
    offerActionButton = (
      <AnvaOfferSelectionOverviewButton
        sessionId={sessionId}
        isPlural={
          !!session?.anva_products && session?.anva_products?.length > 1
        }
      />
    );
  } else if (isYellowhiveFlow) {
    offerActionButton = <YellowhiveSelectionOverviewEmailOfferButton />;
  }

  // Avoid complex Chakra union types by wrapping in Box
  return (
    <div
      style={{
        display: 'flex',
        gap: '1rem',
        marginLeft: 'auto',
        alignItems: 'center',
      }}
    >
      {offerActionButton}
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore TS2590: Expression produces a union type that is too complex to represent. */}
      <Button
        onClick={handleNavigation}
        rightIcon={<Icon name="GeneralActionsArrowRight" />}
        isLoading={isLoading}
      >
        {t('FLOWS_PRODUCTS_SELECTION-OVERVIEW_ACTION')}
      </Button>
    </div>
  );
};

export default BottomBarSelectionOverviewActions;
